/**
 * AnamSession — Wraps @anam-ai/js-sdk for avatar rendering in BYO-audio mode.
 *
 * GPT Realtime API handles all STT/LLM/TTS. Anam is used only for:
 * - Displaying the lip-synced avatar video via WebRTC
 * - Receiving PCM audio chunks (from GPT Realtime) and playing them back
 *   with matching lip-sync via createAgentAudioInputStream
 *
 * Anam's own STT pipeline is disabled via muteInputAudio() immediately after connect.
 */

import { createClient } from '@anam-ai/js-sdk'
import type AnamClient from '@anam-ai/js-sdk/dist/module/AnamClient'
import { AnamEvent } from '@anam-ai/js-sdk'
import type { AgentAudioInputStream } from '@anam-ai/js-sdk/dist/module/types/AgentAudioInputStream'

export interface AnamSessionOptions {
  sessionToken: string
}

let videoIdCounter = 0

export class AnamSession {
  // --- Callbacks ---
  onVideoReady: ((el: HTMLVideoElement) => void) | null = null
  onConnected: (() => void) | null = null
  onDisconnected: ((reason: string) => void) | null = null
  onTalkStreamInterrupted: (() => void) | null = null
  onError: ((err: Error) => void) | null = null

  // --- Internal ---
  private client: AnamClient
  private audioInputStream: AgentAudioInputStream | null = null
  private videoElement: HTMLVideoElement | null = null
  private _connected = false

  constructor(opts: AnamSessionOptions) {
    this.client = createClient(opts.sessionToken, {
      disableInputAudio: true,
      metrics: { disableClientMetrics: true },
    })
  }

  get isConnected(): boolean {
    return this._connected
  }

  get videoEl(): HTMLVideoElement | null {
    return this.videoElement
  }

  // -----------------------------------------------------------------------
  // Public API
  // -----------------------------------------------------------------------

  async connect(): Promise<void> {
    try {
      // Create a video element for the avatar stream
      const videoId = `anam-avatar-video-${++videoIdCounter}`
      this.videoElement = document.createElement('video')
      this.videoElement.id = videoId
      this.videoElement.autoplay = true
      this.videoElement.playsInline = true
      this.videoElement.style.cssText = `
        position: fixed;
        bottom: 0;
        left: 50%;
        transform: translateX(-50%);
        width: 280px;
        height: auto;
        z-index: 2147483646;
        pointer-events: none;
        display: none;
      `
      document.body.appendChild(this.videoElement)

      // Register event listeners before calling stream()
      this.client.addListener(AnamEvent.CONNECTION_ESTABLISHED, () => {
        console.log('[AnamSession] Connection established')
        this._connected = true
        this.onConnected?.()
      })

      this.client.addListener(AnamEvent.VIDEO_PLAY_STARTED, () => {
        console.log('[AnamSession] Video playing')
        if (this.videoElement) {
          // Show the video
          this.videoElement.style.display = ''
          this.onVideoReady?.(this.videoElement)
        }
      })

      this.client.addListener(AnamEvent.TALK_STREAM_INTERRUPTED, () => {
        console.log('[AnamSession] Talk stream interrupted')
        this.onTalkStreamInterrupted?.()
      })

      this.client.addListener(AnamEvent.CONNECTION_CLOSED, (reason) => {
        console.log('[AnamSession] Connection closed:', reason)
        this._connected = false
        this.onDisconnected?.(String(reason))
      })

      // Start streaming — attach Anam's video output to our element
      await this.client.streamToVideoElement(videoId)

      // Create the BYO audio input stream (PCM 16-bit 24kHz mono — exact GPT Realtime match)
      this.audioInputStream = this.client.createAgentAudioInputStream({
        encoding: 'pcm_s16le',
        sampleRate: 24000,
        channels: 1,
      })

      console.log('[AnamSession] BYO audio input stream ready')
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      console.error('[AnamSession] Connection failed:', error.message)
      this.onError?.(error)
      this.close()
      throw error
    }
  }

  /**
   * Send a PCM chunk from GPT Realtime to Anam for lip-sync + audio playback.
   * Accepts Uint8Array (raw PCM 16-bit little-endian, 24kHz, mono).
   */
  sendAudioChunk(pcmData: Uint8Array): void {
    this.audioInputStream?.sendAudioChunk(pcmData)
  }

  /**
   * Signal end of a speaking turn (call on response.done from GPT Realtime).
   */
  endSequence(): void {
    this.audioInputStream?.endSequence()
  }

  /**
   * Interrupt the avatar's current speech (call on user speech detected).
   */
  interrupt(): void {
    this.client.interruptPersona()
  }

  private _closing = false

  /** Close everything. */
  close(): void {
    if (this._closing) return
    this._closing = true
    console.log('[AnamSession] Closing...')

    try { this.client.stopStreaming() } catch {}

    if (this.videoElement) {
      this.videoElement.pause()
      this.videoElement.srcObject = null
      this.videoElement.remove()
      this.videoElement = null
    }

    // Remove any other Anam video elements
    document.querySelectorAll('video[id^="anam-avatar-video"]').forEach(el => {
      ;(el as HTMLVideoElement).pause()
      el.remove()
    })

    this.audioInputStream = null
    this._connected = false
  }
}
